import { useCallback } from "react";
import type { FlightError } from "@b2bportal/air-booking-api";
import {
  type PaymentError,
  type ProductError,
  Product,
  type PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import { useI18nContext } from "@hopper-b2b/i18n";

import {
  getFlightErrorConfig,
  getPurchaseErrorConfig,
  FLIGHT_ERROR_CONFIG,
} from "./config";
import {
  FlightErrorAction,
  type FlightErrorConfig,
  type FlightErrorContext,
} from "./types";

/**
 * Props returned by the hook for rendering error modals
 */
export interface FlightErrorModalProps {
  title: string;
  subtitle: string;
  primaryButtonText: string;
  primaryOnClick: () => void;
  secondaryButtonText?: string;
  secondaryOnClick?: () => void;
  className?: string;
  /** Raw action type for tracking/analytics */
  primaryAction: FlightErrorAction;
  secondaryAction?: FlightErrorAction;
  /** Modal type for tracking */
  modalType: string;
}

/**
 * Action handlers that must be provided by the consuming component
 */
export interface FlightErrorActionHandlers {
  /** Navigate to flight search page */
  navigateToFlightSearch: () => void;
  /** Navigate to My Trips page */
  navigateToMyTrips: () => void;
  /** Navigate to booking confirmation page */
  navigateToConfirmation: () => void;
  /** Open passenger picker/selector */
  openPassengerPicker: () => void;
  /** Go back to passenger info form */
  goBackToPassengerInfo: () => void;
  /** Open passport form */
  openPassportForm: () => void;
  /** Retry the quote operation */
  retryQuote: () => void;
  /** Clear error and close modal */
  clearError: () => void;
}

/**
 * Tenant-specific overrides for flight error configurations.
 * Use this to customize error messages, buttons, or actions for specific tenants.
 */
export interface FlightErrorConfigOverrides {
  /** Override specific FlightError configurations */
  flightErrors?: Partial<Record<FlightError, FlightErrorConfig>>;
  /** Override specific PurchaseError configurations */
  purchaseErrors?: Partial<Record<PurchaseErrorEnum, FlightErrorConfig>>;
  /** Override the default error configuration */
  default?: FlightErrorConfig;
}

/**
 * Hook options
 */
export interface UseFlightErrorConfigOptions {
  /** The context in which errors occur */
  context: FlightErrorContext;
  /** Action handlers for button clicks */
  handlers: FlightErrorActionHandlers;
  /** Whether retry is allowed (affects some error configurations) */
  allowRetry?: boolean;
  /**
   * Tenant-specific overrides for error configurations.
   * These will be merged with the base config, allowing tenants to customize
   * specific error messages, buttons, or actions.
   *
   * @example
   * ```tsx
   * configOverrides: {
   *   flightErrors: {
   *     [FlightError.DuplicateBooking]: {
   *       titleKey: "myTenant.duplicateBookingTitle",
   *       subtitleKey: "myTenant.duplicateBookingSubtitle",
   *       primaryButton: { textKey: "myTenant.viewTrips", action: FlightErrorAction.NavigateToMyTrips },
   *       secondaryButton: { textKey: "myTenant.contactSupport", action: FlightErrorAction.ClearError },
   *     },
   *   },
   * }
   * ```
   */
  configOverrides?: FlightErrorConfigOverrides;
}

/**
 * Maps an action type to its handler function
 */
const mapActionToHandler = (
  action: FlightErrorAction,
  handlers: FlightErrorActionHandlers
): (() => void) => {
  switch (action) {
    case FlightErrorAction.NavigateToFlightSearch:
      return handlers.navigateToFlightSearch;
    case FlightErrorAction.NavigateToMyTrips:
      return handlers.navigateToMyTrips;
    case FlightErrorAction.NavigateToConfirmation:
      return handlers.navigateToConfirmation;
    case FlightErrorAction.OpenPassengerPicker:
      return handlers.openPassengerPicker;
    case FlightErrorAction.GoBackToPassengerInfo:
      return handlers.goBackToPassengerInfo;
    case FlightErrorAction.OpenPassportForm:
      return handlers.openPassportForm;
    case FlightErrorAction.RetryQuote:
      return handlers.retryQuote;
    case FlightErrorAction.ClearError:
    case FlightErrorAction.None:
    default:
      return handlers.clearError;
  }
};

/**
 * Hook to get flight error configuration and map it to modal props
 *
 * @example
 * ```tsx
 * const { getErrorModalProps, getFlightErrorProps, getPurchaseErrorProps } = useFlightErrorConfig({
 *   context: "CART_QUOTE",
 *   handlers: {
 *     navigateToFlightSearch: () => history.push("/flights/search"),
 *     navigateToMyTrips: () => history.push("/trips"),
 *     navigateToConfirmation: () => history.push("/confirmation"),
 *     openPassengerPicker: () => send(CartQuoteEventType.OPEN_PASSENGER_PICKER),
 *     goBackToPassengerInfo: () => send(Event.GO_TO_PASSENGER_SELECT),
 *     openPassportForm: () => send(FlightPassengerEventTypesV2.OPEN_PASSPORT_FORM),
 *     retryQuote: () => send(CartQuoteEventType.RETRY_QUOTE),
 *     clearError: () => send(CartQuoteEventType.CLEAR_CART_QUOTE_ERROR),
 *   },
 * });
 *
 * // Get props for a specific FlightError
 * const props = getFlightErrorProps(FlightError.DuplicateBooking);
 *
 * // Or get props from a PurchaseError
 * const props = getPurchaseErrorProps(purchaseError);
 * ```
 *
 * @example With tenant overrides
 * ```tsx
 * const { getPurchaseErrorProps } = useFlightErrorConfig({
 *   context: "CART_QUOTE",
 *   handlers: { ... },
 *   configOverrides: {
 *     flightErrors: {
 *       [FlightError.PaymentFailed]: {
 *         titleKey: "perkspot.paymentFailedTitle",
 *         subtitleKey: "perkspot.paymentFailedSubtitle",
 *         primaryButton: { textKey: "perkspot.retry", action: FlightErrorAction.RetryQuote },
 *         secondaryButton: { textKey: "perkspot.contactSupport", action: FlightErrorAction.ClearError },
 *       },
 *     },
 *   },
 * });
 * ```
 */
export const useFlightErrorConfig = ({
  context,
  handlers,
  allowRetry = true,
  configOverrides,
}: UseFlightErrorConfigOptions) => {
  const { t } = useI18nContext();

  /**
   * Get merged flight error config with overrides
   */
  const getMergedFlightErrorConfig = useCallback(
    (errorCode: FlightError): FlightErrorConfig => {
      // Check for override first
      if (configOverrides?.flightErrors?.[errorCode]) {
        return configOverrides.flightErrors[errorCode];
      }
      // Fall back to base config
      return getFlightErrorConfig(context, errorCode);
    },
    [context, configOverrides]
  );

  /**
   * Get merged purchase error config with overrides
   */
  const getMergedPurchaseErrorConfig = useCallback(
    (category: PurchaseErrorEnum): FlightErrorConfig | undefined => {
      // Check for override first
      if (configOverrides?.purchaseErrors?.[category]) {
        return configOverrides.purchaseErrors[category];
      }
      // Fall back to base config
      return getPurchaseErrorConfig(context, category);
    },
    [context, configOverrides]
  );

  /**
   * Get the default config (with potential override)
   */
  const getDefaultConfig = useCallback((): FlightErrorConfig => {
    if (configOverrides?.default) {
      return configOverrides.default;
    }
    return FLIGHT_ERROR_CONFIG[context].default;
  }, [context, configOverrides]);

  /**
   * Converts a FlightErrorConfig to FlightErrorModalProps
   */
  const configToProps = useCallback(
    (config: FlightErrorConfig, modalType: string): FlightErrorModalProps => {
      const primaryAction = config.primaryButton.action;
      const secondaryAction = config.secondaryButton?.action;

      return {
        title: t(config.titleKey),
        subtitle: t(config.subtitleKey),
        primaryButtonText: t(config.primaryButton.textKey),
        primaryOnClick: mapActionToHandler(primaryAction, handlers),
        primaryAction,
        ...(config.secondaryButton && {
          secondaryButtonText: t(config.secondaryButton.textKey),
          secondaryOnClick: mapActionToHandler(
            config.secondaryButton.action,
            handlers
          ),
          secondaryAction,
        }),
        className: config.className,
        modalType,
      };
    },
    [t, handlers]
  );

  /**
   * Get modal props for a specific FlightError code
   */
  const getFlightErrorProps = useCallback(
    (errorCode: FlightError): FlightErrorModalProps => {
      const config = getMergedFlightErrorConfig(errorCode);
      return configToProps(config, `FlightError.${errorCode}`);
    },
    [getMergedFlightErrorConfig, configToProps]
  );

  /**
   * Get modal props from a ProductError
   */
  const getProductErrorProps = useCallback(
    (error: ProductError): FlightErrorModalProps => {
      const defaultConfig = getDefaultConfig();

      switch (error.value.type) {
        case Product.Flight:
        case Product.MultiProviderFlight:
        case Product.Seats: {
          const flightError = error.value.value as FlightError;
          return getFlightErrorProps(flightError);
        }
        default:
          return configToProps(
            defaultConfig,
            `ProductError.${error.value.type}`
          );
      }
    },
    [getDefaultConfig, configToProps, getFlightErrorProps]
  );

  /**
   * Get modal props from a PaymentError
   */
  const getPaymentErrorProps = useCallback(
    (_error: PaymentError): FlightErrorModalProps => {
      const config =
        getMergedPurchaseErrorConfig(PurchaseErrorEnum.PaymentError) ??
        getDefaultConfig();
      return configToProps(config, PurchaseErrorEnum.PaymentError);
    },
    [getMergedPurchaseErrorConfig, getDefaultConfig, configToProps]
  );

  /**
   * Get modal props from a PurchaseError
   */
  const getPurchaseErrorProps = useCallback(
    (error: PurchaseError): FlightErrorModalProps => {
      switch (error.Error) {
        case PurchaseErrorEnum.ProductError:
          return getProductErrorProps(error as ProductError);
        case PurchaseErrorEnum.PaymentError:
          return getPaymentErrorProps(error as PaymentError);
        case PurchaseErrorEnum.NoAvailability:
        case PurchaseErrorEnum.AuthError:
        case PurchaseErrorEnum.ExpiredFulfillToken:
        case PurchaseErrorEnum.ExpiredQuoteToken:
        case PurchaseErrorEnum.FraudAutoReject:
        case PurchaseErrorEnum.InActivity:
        case PurchaseErrorEnum.InvalidSession:
        case PurchaseErrorEnum.ErrorCode:
        case PurchaseErrorEnum.NoPaymentsProvided:
        case PurchaseErrorEnum.NonDistinctPayments:
        default: {
          const config =
            getMergedPurchaseErrorConfig(error.Error) ?? getDefaultConfig();
          return configToProps(config, error.Error);
        }
      }
    },
    [
      configToProps,
      getProductErrorProps,
      getPaymentErrorProps,
      getMergedPurchaseErrorConfig,
      getDefaultConfig,
    ]
  );

  /**
   * Get the default error modal props
   */
  const getDefaultProps = useCallback((): FlightErrorModalProps => {
    const config = getDefaultConfig();
    return configToProps(config, "default");
  }, [getDefaultConfig, configToProps]);

  return {
    /** Get modal props for a specific FlightError code */
    getFlightErrorProps,
    /** Get modal props from a ProductError */
    getProductErrorProps,
    /** Get modal props from a PaymentError */
    getPaymentErrorProps,
    /** Get modal props from any PurchaseError */
    getPurchaseErrorProps,
    /** Get the default error modal props */
    getDefaultProps,
  };
};
